import { TransformControls } from '@react-three/drei';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type CameraControlsImpl from 'camera-controls';
import { useThree } from '@react-three/fiber';

import type {
  GizmoRotationAxis,
  RotateSpaceMode,
  ViewerToolMode,
} from '../../../../cartographerStore';
import type { Skeleton } from '../../../../lib/skeleton/core';
import { AXIS_COLOR_X, AXIS_COLOR_Y, AXIS_COLOR_Z } from '../../constants';
import { clampManipulatorScale } from '../../shared';

interface RotationManipulatorProps {
  skeleton: Skeleton;
  selectedBoneIndex: number | null;
  viewerToolMode: ViewerToolMode;
  rotateSpaceMode: RotateSpaceMode;
  poseVersion: number;
  manipulatorScale: number;
  onApplyBoneRotateFromWorldQuaternion?: (
    boneIndex: number,
    worldQuaternion: THREE.Quaternion,
  ) => void;
  onApplyBoneRotateFromGimbalDelta?: (
    boneIndex: number,
    axis: GizmoRotationAxis,
    deltaDegrees: number,
  ) => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

const GIZMO_SPACE_BY_MODE: Record<RotateSpaceMode, 'local'> = {
  local: 'local',
  gimbal: 'local',
};

const GIMBAL_DELTA_EPSILON_DEGREES = 1e-4;
const GIMBAL_DELTA_EPSILON_RADIANS = THREE.MathUtils.degToRad(
  GIMBAL_DELTA_EPSILON_DEGREES,
);
const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Y = new THREE.Vector3(0, 1, 0);
const AXIS_Z = new THREE.Vector3(0, 0, 1);
const OVERLAY_KEY = '_cartographerOverlay';
const MARKER_SPHERE_RADIUS = 0.05;
const E_RING_COLOR = new THREE.Color(0xffffff);
const AXIS_RING_COLOR_X = new THREE.Color(AXIS_COLOR_X);
const AXIS_RING_COLOR_Y = new THREE.Color(AXIS_COLOR_Y);
const AXIS_RING_COLOR_Z = new THREE.Color(AXIS_COLOR_Z);
const E_RING_BASE_OPACITY = 0.5;
const E_RING_DIM_OPACITY = 0.15;
const E_RING_DEFAULT_THICKNESS = 0.004;
const E_RING_HIGHLIGHT_THICKNESS = 0.01;
const AXIS_RING_DEFAULT_THICKNESS = 0.005;
const AXIS_RING_HIGHLIGHT_THICKNESS = 0.015;
const ROTATE_GIZMO_BASE_SIZE = 0.72;

type DraggingChangedEvent = { value: boolean };

type TransformControlsHandle = {
  axis: string | null;
  gizmo?: {
    gizmo?: {
      rotate?: THREE.Object3D;
    };
  };
  addEventListener: (
    type: 'dragging-changed',
    listener: (event: DraggingChangedEvent) => void,
  ) => void;
  removeEventListener: (
    type: 'dragging-changed',
    listener: (event: DraggingChangedEvent) => void,
  ) => void;
  traverse: (callback: (object: THREE.Object3D) => void) => void;
};

type RaycastableObject = THREE.Object3D & {
  raycast?: (...args: unknown[]) => void;
};

type PendingLocalRotation = {
  boneIndex: number;
  worldQuaternion: THREE.Quaternion;
};

type PendingGimbalDelta = {
  boneIndex: number;
  axis: GizmoRotationAxis;
  deltaDegrees: number;
};

function resolveGizmoAxis(axis: string | null): GizmoRotationAxis | null {
  switch (axis) {
    case 'X':
      return 'x';
    case 'Y':
      return 'y';
    case 'Z':
      return 'z';
    default:
      return null;
  }
}

function normalizeRadians(angleRadians: number): number {
  if (!Number.isFinite(angleRadians)) return 0;
  let normalized = angleRadians;
  while (normalized > Math.PI) normalized -= Math.PI * 2;
  while (normalized < -Math.PI) normalized += Math.PI * 2;
  return normalized;
}

function getAxisVector(axis: GizmoRotationAxis): THREE.Vector3 {
  switch (axis) {
    case 'x':
      return AXIS_X;
    case 'y':
      return AXIS_Y;
    case 'z':
      return AXIS_Z;
  }
}

function resolveAxisRingColor(name: string): THREE.Color | null {
  switch (name) {
    case 'X':
      return AXIS_RING_COLOR_X;
    case 'Y':
      return AXIS_RING_COLOR_Y;
    case 'Z':
      return AXIS_RING_COLOR_Z;
    default:
      return null;
  }
}

function setObjectMaterialVisibility(
  object: THREE.Object3D,
  isVisible: boolean,
): void {
  const material =
    (object as THREE.Object3D & {
      material?: THREE.Material | THREE.Material[];
    }).material ?? null;
  if (!material) return;
  if (Array.isArray(material)) {
    for (const nextMaterial of material) {
      nextMaterial.visible = isVisible;
    }
    return;
  }
  material.visible = isVisible;
}

function buildMarkerSphereGeometry(
  bakedGeometry: THREE.BufferGeometry,
): THREE.BufferGeometry {
  bakedGeometry.computeBoundingSphere();
  const center = bakedGeometry.boundingSphere?.center ?? new THREE.Vector3();
  const sphere = new THREE.SphereGeometry(MARKER_SPHERE_RADIUS, 12, 12);
  sphere.translate(center.x, center.y, center.z);
  return sphere;
}

function buildTickLineGeometry(
  bakedGeometry: THREE.BufferGeometry,
): THREE.BufferGeometry {
  bakedGeometry.computeBoundingBox();
  const box = bakedGeometry.boundingBox!;
  const center = new THREE.Vector3();
  box.getCenter(center);
  const size = new THREE.Vector3();
  box.getSize(size);

  const isXPrincipal = size.x > size.y;
  const halfLength = Math.max(size.x, size.y) / 2;
  const halfWidth = 0.003;

  const vertices = isXPrincipal
    ? [
        center.x - halfLength, center.y - halfWidth, center.z,
        center.x - halfLength, center.y + halfWidth, center.z,
        center.x + halfLength, center.y - halfWidth, center.z,
        center.x + halfLength, center.y + halfWidth, center.z,
      ]
    : [
        center.x - halfWidth, center.y - halfLength, center.z,
        center.x + halfWidth, center.y - halfLength, center.z,
        center.x - halfWidth, center.y + halfLength, center.z,
        center.x + halfWidth, center.y + halfLength, center.z,
      ];

  const geom = new THREE.BufferGeometry();
  geom.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  geom.setIndex([0, 1, 2, 2, 1, 3]);
  geom.computeVertexNormals();
  return geom;
}

function buildTubeFromLineVertices(
  lineGeometry: THREE.BufferGeometry,
  tubeRadius: number,
): THREE.TubeGeometry | null {
  const posAttr = lineGeometry.getAttribute('position');
  if (!posAttr || posAttr.count < 2) return null;

  const points: THREE.Vector3[] = [];
  for (let i = 0; i < posAttr.count; i++) {
    points.push(
      new THREE.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i)),
    );
  }

  const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal');
  return new THREE.TubeGeometry(curve, 64, tubeRadius, 8, false);
}

type GizmoMaterial = THREE.Material & {
  color: THREE.Color;
};

function getObjectMaterial(object: THREE.Object3D): GizmoMaterial | null {
  const holder = object as THREE.Object3D & {
    material?: THREE.Material | THREE.Material[];
  };
  if (!holder.material || Array.isArray(holder.material)) return null;
  const mat = holder.material as THREE.Material & { color?: THREE.Color };
  if (!mat.color) return null;
  return mat as GizmoMaterial;
}

function getRotateGizmoRoot(
  controls: TransformControlsHandle,
): THREE.Object3D | null {
  return controls.gizmo?.gizmo?.rotate ?? null;
}

function getParentWorldQuaternion(
  skeleton: Skeleton,
  selectedBoneIndex: number,
): THREE.Quaternion {
  const selectedBone = skeleton.bone(selectedBoneIndex);
  const parentWorldMatrix =
    selectedBone.parent === null
      ? skeleton.rootParentWorld
      : skeleton.bone(selectedBone.parent).posedWorld;
  const parentWorldQuaternion = new THREE.Quaternion();
  parentWorldMatrix.decompose(
    new THREE.Vector3(),
    parentWorldQuaternion,
    new THREE.Vector3(),
  );
  return parentWorldQuaternion.normalize();
}

function signedAxisDeltaRadians(
  deltaLocalQuaternion: THREE.Quaternion,
  axis: GizmoRotationAxis,
): number {
  const normalizedDelta = deltaLocalQuaternion.clone().normalize();
  const safeW = THREE.MathUtils.clamp(normalizedDelta.w, -1, 1);
  const angle = 2 * Math.acos(safeW);
  if (!Number.isFinite(angle) || angle < GIMBAL_DELTA_EPSILON_RADIANS) {
    return 0;
  }

  const sinHalf = Math.sqrt(Math.max(1 - safeW * safeW, 0));
  if (sinHalf < 1e-6) return 0;

  const deltaAxis = new THREE.Vector3(
    normalizedDelta.x / sinHalf,
    normalizedDelta.y / sinHalf,
    normalizedDelta.z / sinHalf,
  ).normalize();
  const direction = deltaAxis.dot(getAxisVector(axis));
  if (!Number.isFinite(direction) || Math.abs(direction) < 1e-6) return 0;

  return normalizeRadians(angle * Math.sign(direction));
}

export default function RotationManipulator({
  skeleton,
  selectedBoneIndex,
  viewerToolMode,
  rotateSpaceMode,
  poseVersion,
  manipulatorScale,
  onApplyBoneRotateFromWorldQuaternion,
  onApplyBoneRotateFromGimbalDelta,
  onDraggingChange,
}: RotationManipulatorProps) {
  const cameraControls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const [anchorObject, setAnchorObject] = useState<THREE.Group | null>(null);
  const [transformControls, setTransformControls] =
    useState<TransformControlsHandle | null>(null);
  const isDraggingRef = useRef(false);
  const queuedLocalRotationRef = useRef<PendingLocalRotation | null>(null);
  const queuedGimbalDeltasRef = useRef<PendingGimbalDelta[]>([]);
  const gimbalPreviousWorldQuaternionRef = useRef<THREE.Quaternion | null>(null);
  const flushRafRef = useRef<number | null>(null);
  const onApplyWorldQuaternionRef = useRef(onApplyBoneRotateFromWorldQuaternion);
  const onApplyGimbalDeltaRef = useRef(onApplyBoneRotateFromGimbalDelta);
  const isRotateMode = viewerToolMode === 'rotate';
  const hasSelection = selectedBoneIndex !== null;
  const canShowGizmo = isRotateMode && hasSelection;
  const transformSpace = GIZMO_SPACE_BY_MODE[rotateSpaceMode];
  const neutralScale = useMemo(() => new THREE.Vector3(1, 1, 1), []);
  const safeManipulatorScale = clampManipulatorScale(manipulatorScale);

  const handleAnchorRef = useCallback((nextAnchor: THREE.Group | null) => {
    setAnchorObject((currentAnchor) =>
      currentAnchor === nextAnchor ? currentAnchor : nextAnchor,
    );
  }, []);

  const handleTransformControlsRef = useCallback(
    (nextControls: unknown) => {
      const resolvedControls = nextControls as TransformControlsHandle | null;
      setTransformControls((currentControls) =>
        currentControls === resolvedControls ? currentControls : resolvedControls,
      );
    },
    [],
  );

  const clearQueuedUpdates = useCallback(() => {
    queuedLocalRotationRef.current = null;
    queuedGimbalDeltasRef.current = [];
  }, []);

  useEffect(() => {
    onApplyWorldQuaternionRef.current = onApplyBoneRotateFromWorldQuaternion;
    onApplyGimbalDeltaRef.current = onApplyBoneRotateFromGimbalDelta;
  }, [onApplyBoneRotateFromGimbalDelta, onApplyBoneRotateFromWorldQuaternion]);

  const applyQueuedUpdates = useCallback(() => {
    const pendingWorldRotation = queuedLocalRotationRef.current;
    if (pendingWorldRotation) {
      onApplyWorldQuaternionRef.current?.(
        pendingWorldRotation.boneIndex,
        pendingWorldRotation.worldQuaternion,
      );
    }

    const pendingGimbalDeltas = queuedGimbalDeltasRef.current;
    if (pendingGimbalDeltas.length > 0) {
      const applyGimbalDelta = onApplyGimbalDeltaRef.current;
      if (applyGimbalDelta) {
        for (const pendingDelta of pendingGimbalDeltas) {
          if (
            Math.abs(pendingDelta.deltaDegrees) <
            GIMBAL_DELTA_EPSILON_DEGREES
          ) {
            continue;
          }
          applyGimbalDelta(
            pendingDelta.boneIndex,
            pendingDelta.axis,
            pendingDelta.deltaDegrees,
          );
        }
      }
    }

    clearQueuedUpdates();
  }, [clearQueuedUpdates]);

  const flushQueuedUpdates = useCallback(() => {
    if (flushRafRef.current !== null) {
      cancelAnimationFrame(flushRafRef.current);
      flushRafRef.current = null;
    }
    applyQueuedUpdates();
  }, [applyQueuedUpdates]);

  const scheduleQueuedFlush = useCallback(() => {
    if (flushRafRef.current !== null) return;
    flushRafRef.current = requestAnimationFrame(() => {
      flushRafRef.current = null;
      applyQueuedUpdates();
    });
  }, [applyQueuedUpdates]);

  const queueGimbalDelta = useCallback(
    (boneIndex: number, axis: GizmoRotationAxis, deltaDegrees: number) => {
      if (Math.abs(deltaDegrees) < GIMBAL_DELTA_EPSILON_DEGREES) return;

      const pendingDeltas = queuedGimbalDeltasRef.current;
      const lastPendingDelta = pendingDeltas[pendingDeltas.length - 1];
      if (
        lastPendingDelta &&
        lastPendingDelta.boneIndex === boneIndex &&
        lastPendingDelta.axis === axis
      ) {
        lastPendingDelta.deltaDegrees += deltaDegrees;
        return;
      }

      pendingDeltas.push({ boneIndex, axis, deltaDegrees });
    },
    [],
  );

  const beginDragging = useCallback(() => {
    if (isDraggingRef.current) return;
    isDraggingRef.current = true;
    gimbalPreviousWorldQuaternionRef.current = anchorObject
      ? anchorObject.quaternion.clone()
      : null;
    onDraggingChange?.(true);
    if (cameraControls) cameraControls.enabled = false;
  }, [anchorObject, cameraControls, onDraggingChange]);

  const endDragging = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    flushQueuedUpdates();
    gimbalPreviousWorldQuaternionRef.current = null;
    onDraggingChange?.(false);
    if (cameraControls) cameraControls.enabled = true;
  }, [cameraControls, flushQueuedUpdates, onDraggingChange]);

  useEffect(() => {
    if (!canShowGizmo) return;
    if (selectedBoneIndex === null) return;
    if (selectedBoneIndex < 0 || selectedBoneIndex >= skeleton.bones.length) return;
    if (!anchorObject) return;
    if (isDraggingRef.current) return;

    const selectedBone = skeleton.bone(selectedBoneIndex);
    selectedBone.posedWorld.decompose(
      anchorObject.position,
      anchorObject.quaternion,
      neutralScale,
    );
    anchorObject.scale.set(1, 1, 1);
    anchorObject.updateMatrixWorld(true);
  }, [
    anchorObject,
    canShowGizmo,
    neutralScale,
    poseVersion,
    selectedBoneIndex,
    skeleton,
    viewerToolMode,
  ]);

  useEffect(() => {
    if (canShowGizmo) return;
    endDragging();
  }, [canShowGizmo, endDragging]);

  useEffect(() => {
    if (!transformControls) return;
    const showCameraRelativeHandle = rotateSpaceMode === 'local';
    transformControls.traverse((object) => {
      if (object.name !== 'E') return;
      const raycastable = object as RaycastableObject;
      const originalRaycast = raycastable.userData
        ._cartographerOriginalRaycast as RaycastableObject['raycast'] | undefined;
      if (!originalRaycast && raycastable.raycast) {
        raycastable.userData._cartographerOriginalRaycast = raycastable.raycast;
      }
      if (showCameraRelativeHandle) {
        if (originalRaycast) {
          raycastable.raycast = originalRaycast;
        }
      } else {
        raycastable.raycast = () => {};
      }
      const overlay = object.userData[OVERLAY_KEY] as THREE.Object3D | undefined;
      if (overlay) {
        overlay.visible = showCameraRelativeHandle;
      } else {
        setObjectMaterialVisibility(object, showCameraRelativeHandle);
      }
      object.visible = showCameraRelativeHandle;
    });
  }, [rotateSpaceMode, transformControls]);

  useEffect(() => {
    if (!transformControls) return;
    const controls = transformControls;
    const rotateGizmo = getRotateGizmoRoot(controls);
    if (!rotateGizmo) return;

    const cleanupFns: (() => void)[] = [];
    const handles: THREE.Object3D[] = [];
    rotateGizmo.traverse((object) => {
      if (
        object.name === 'X' ||
        object.name === 'Y' ||
        object.name === 'Z' ||
        object.name === 'E'
      ) {
        handles.push(object);
      }
    });

    for (const handle of handles) {
      const name = handle.name;
      const isE = name === 'E';
      const axisRingColor = resolveAxisRingColor(name);

      // --- Line handles (arc rings) → tube overlays as grandchildren ---
      const lineObj = handle as THREE.Line & { isLine?: boolean };
      if (lineObj.isLine) {
        const lineMat = lineObj.material as THREE.LineBasicMaterial;
        const savedVisible = lineMat.visible;
        lineMat.visible = false;

        const defaultRadius = isE
          ? E_RING_DEFAULT_THICKNESS
          : AXIS_RING_DEFAULT_THICKNESS;
        const highlightRadius = isE
          ? E_RING_HIGHLIGHT_THICKNESS
          : AXIS_RING_HIGHLIGHT_THICKNESS;

        const defaultGeom = buildTubeFromLineVertices(
          lineObj.geometry,
          defaultRadius,
        );
        const highlightGeom = buildTubeFromLineVertices(
          lineObj.geometry,
          highlightRadius,
        );
        if (!defaultGeom || !highlightGeom) {
          lineMat.visible = savedVisible;
          continue;
        }

        const meshMat = new THREE.MeshBasicMaterial({
          depthTest: lineMat.depthTest,
          depthWrite: lineMat.depthWrite,
          transparent: lineMat.transparent,
          color: isE
            ? E_RING_COLOR.clone()
            : (axisRingColor?.clone() ?? lineMat.color.clone()),
          opacity: isE ? E_RING_BASE_OPACITY : lineMat.opacity,
          side: THREE.DoubleSide,
          toneMapped: false,
          fog: false,
        });
        const baseColor = isE
          ? E_RING_COLOR.clone()
          : (axisRingColor?.clone() ?? meshMat.color.clone());

        const tubeMesh = new THREE.Mesh(defaultGeom, meshMat);
        tubeMesh.renderOrder = lineObj.renderOrder;
        handle.userData[OVERLAY_KEY] = tubeMesh;
        lineObj.add(tubeMesh);

        if (isE) {
          tubeMesh.onBeforeRender = () => {
            meshMat.color.copy(E_RING_COLOR);
            if (controls.axis === 'E') {
              meshMat.opacity = 1;
              if (tubeMesh.geometry !== highlightGeom)
                tubeMesh.geometry = highlightGeom;
            } else if (controls.axis) {
              meshMat.opacity = E_RING_DIM_OPACITY;
              if (tubeMesh.geometry !== defaultGeom)
                tubeMesh.geometry = defaultGeom;
            } else {
              meshMat.opacity = E_RING_BASE_OPACITY;
              if (tubeMesh.geometry !== defaultGeom)
                tubeMesh.geometry = defaultGeom;
            }
          };
        } else {
          tubeMesh.onBeforeRender = () => {
            meshMat.color.copy(baseColor);
            if (controls.axis === name) {
              if (tubeMesh.geometry !== highlightGeom)
                tubeMesh.geometry = highlightGeom;
            } else {
              if (tubeMesh.geometry !== defaultGeom)
                tubeMesh.geometry = defaultGeom;
            }
          };
        }

        cleanupFns.push(() => {
          lineObj.remove(tubeMesh);
          delete handle.userData[OVERLAY_KEY];
          lineMat.visible = savedVisible;
          defaultGeom.dispose();
          highlightGeom.dispose();
          meshMat.dispose();
        });
        continue;
      }

      // --- Mesh handles → overlay children ---
      const mesh = handle as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) continue;
      const material = getObjectMaterial(mesh);
      if (!material) continue;

      const savedMaterialVisible = material.visible;
      material.visible = false;

      // E-handle CylinderGeometry → tick mark overlay
      if (isE && mesh.geometry.type === 'CylinderGeometry') {
        const tickGeom = buildTickLineGeometry(mesh.geometry);
        const tickMat = new THREE.MeshBasicMaterial({
          color: E_RING_COLOR.clone(),
          opacity: E_RING_BASE_OPACITY,
          transparent: true,
          side: THREE.DoubleSide,
          depthTest: material.depthTest,
          depthWrite: material.depthWrite,
          toneMapped: false,
          fog: false,
        });
        const tickMesh = new THREE.Mesh(tickGeom, tickMat);
        tickMesh.renderOrder = mesh.renderOrder;
        handle.userData[OVERLAY_KEY] = tickMesh;
        mesh.add(tickMesh);

        tickMesh.onBeforeRender = () => {
          tickMat.color.copy(E_RING_COLOR);
          if (controls.axis === 'E') {
            tickMat.opacity = 1;
          } else if (controls.axis) {
            tickMat.opacity = E_RING_DIM_OPACITY;
          } else {
            tickMat.opacity = E_RING_BASE_OPACITY;
          }
        };

        cleanupFns.push(() => {
          mesh.remove(tickMesh);
          delete handle.userData[OVERLAY_KEY];
          tickGeom.dispose();
          tickMat.dispose();
          material.visible = savedMaterialVisible;
        });
        continue;
      }

      // Axis markers (X/Y/Z meshes) → sphere overlay
      if (!isE) {
        const sphereGeom = buildMarkerSphereGeometry(mesh.geometry);
        const baseColor = axisRingColor?.clone() ?? material.color.clone();
        const sphereMat = new THREE.MeshBasicMaterial({
          color: baseColor.clone(),
          opacity: material.opacity,
          transparent: material.transparent,
          depthTest: material.depthTest,
          depthWrite: material.depthWrite,
          side: THREE.DoubleSide,
          toneMapped: false,
          fog: false,
        });
        const sphereMesh = new THREE.Mesh(sphereGeom, sphereMat);
        sphereMesh.renderOrder = mesh.renderOrder;
        handle.userData[OVERLAY_KEY] = sphereMesh;
        mesh.add(sphereMesh);

        sphereMesh.onBeforeRender = () => {
          sphereMat.color.copy(baseColor);
        };

        cleanupFns.push(() => {
          mesh.remove(sphereMesh);
          delete handle.userData[OVERLAY_KEY];
          sphereGeom.dispose();
          sphereMat.dispose();
          material.visible = savedMaterialVisible;
        });
        continue;
      }

      // Fallback: hide original, no custom overlay
      cleanupFns.push(() => {
        material.visible = savedMaterialVisible;
      });
    }

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [transformControls]);

  useEffect(() => {
    if (!transformControls) return;

    const handleDraggingChanged = (event: DraggingChangedEvent) => {
      if (event.value) {
        beginDragging();
      } else {
        endDragging();
      }
    };

    transformControls.addEventListener(
      'dragging-changed',
      handleDraggingChanged,
    );
    return () => {
      transformControls.removeEventListener(
        'dragging-changed',
        handleDraggingChanged,
      );
    };
  }, [
    beginDragging,
    endDragging,
    transformControls,
  ]);

  useEffect(
    () => () => {
      endDragging();
    },
    [endDragging],
  );

  if (!canShowGizmo) return null;

  return (
    <>
      <group ref={handleAnchorRef} />
      {anchorObject && (
        <TransformControls
          ref={handleTransformControlsRef}
          object={anchorObject}
          mode="rotate"
          space={transformSpace}
          size={ROTATE_GIZMO_BASE_SIZE * safeManipulatorScale}
          onObjectChange={() => {
            if (!isDraggingRef.current) return;
            if (selectedBoneIndex === null) return;
            if (selectedBoneIndex < 0 || selectedBoneIndex >= skeleton.bones.length) {
              return;
            }

            const nextWorldQuaternion = anchorObject.quaternion.clone();
            if (rotateSpaceMode === 'gimbal') {
              queuedLocalRotationRef.current = null;

              const previousWorldQuaternion = gimbalPreviousWorldQuaternionRef.current;
              gimbalPreviousWorldQuaternionRef.current = nextWorldQuaternion.clone();
              if (!previousWorldQuaternion) return;

              const parentWorldQuaternion = getParentWorldQuaternion(
                skeleton,
                selectedBoneIndex,
              );
              const parentInverseQuaternion = parentWorldQuaternion.clone().invert();
              const previousLocalQuaternion = parentInverseQuaternion
                .clone()
                .multiply(previousWorldQuaternion)
                .normalize();
              const nextLocalQuaternion = parentInverseQuaternion
                .clone()
                .multiply(nextWorldQuaternion)
                .normalize();

              const activeAxis = resolveGizmoAxis(transformControls?.axis ?? null);
              if (!activeAxis) return;
              const deltaLocalQuaternion = previousLocalQuaternion
                .clone()
                .invert()
                .multiply(nextLocalQuaternion)
                .normalize();
              const deltaRadians = signedAxisDeltaRadians(
                deltaLocalQuaternion,
                activeAxis,
              );
              queueGimbalDelta(
                selectedBoneIndex,
                activeAxis,
                THREE.MathUtils.radToDeg(deltaRadians),
              );
              scheduleQueuedFlush();
              return;
            }

            queuedGimbalDeltasRef.current = [];
            queuedLocalRotationRef.current = {
              boneIndex: selectedBoneIndex,
              worldQuaternion: nextWorldQuaternion,
            };
            scheduleQueuedFlush();
          }}
        />
      )}
    </>
  );
}
